import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '@/core/auth/AuthContext';
import { userCanAccessPath, userHasPermission } from '@/core/auth/permissions';
import { useLanguage } from '@/core/i18n/LanguageContext';
import { LoadingState } from '@/shared/components/LoadingState';
import { ErrorState } from '@/shared/components/ErrorState';

const ProtectedRoute = ({ children, requiredRoles = [], requiredPermissions = [] }) => {
  const { user, loading } = useAuth();
  const { t } = useLanguage();
  const location = useLocation();
  const pathname = location.pathname || '';

  if (loading) {
    return (
      <LoadingState
        variant="box"
        className="flex items-center justify-center h-screen"
        messageClassName="text-xl"
        message={t('loading')}
      />
    );
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  if (requiredPermissions.length > 0) {
    const missing = requiredPermissions.some((code) => !userHasPermission(user, code));
    if (missing) {
      return (
        <ErrorState
          className="flex items-center justify-center h-screen"
          messageClassName="text-xl text-red-600"
          message={t('accessDeniedPage')}
        />
      );
    }
  } else if (requiredRoles.length > 0 && !requiredRoles.includes(user.role)) {
    // Legacy role gate only when no permission list is declared on the route.
    return (
      <ErrorState
        className="flex items-center justify-center h-screen"
        messageClassName="text-xl text-red-600"
        message={t('accessDeniedPage')}
      />
    );
  }

  if (!userCanAccessPath(user, pathname)) {
    const fallback =
      (user.allowed_routes && user.allowed_routes[0]) || '/dashboard';
    if (pathname !== fallback) {
      return <Navigate to={fallback} replace />;
    }
    return (
      <ErrorState
        className="flex items-center justify-center h-screen"
        messageClassName="text-xl text-red-600"
        message={t('accessDeniedPage')}
      />
    );
  }

  return children;
};

export default ProtectedRoute;
